<template>
	<div>
		<div class="clearfix mb-2">
			<h2 class="float-left">{{ $t('third-party-data-import') }}</h2>
		</div>

		<StepIndicator v-if="step <= 3" :steps="stepLabels" :current-step="step" class="mb-4" />

		<StepPreview
			v-if="step === 1"
			:title="$t('upload-third-party-data-csv')"
			:loading-preview="loadingPreview"
			:preview="preview"
			:show-back="false"
			@file-selected="onFileSelected"
			@next="step = 2"
		/>

		<StepMapColumns
			v-if="step === 2"
			:csv-cols="csvCols"
			:existing-cols="existingCols"
			:existing-cols-arr="existingColsArr"
			:col-assign="colAssign"
			:datatype="datatype"
			:created-cols="createdCols"
			:student-id-column-index.sync="studentIdColumnIndex"
			:mapping-valid="mappingValid"
			:preview-rows="preview ? preview.rows : []"
			@select-change="handleSelection"
			@next="onMapColumnsNext"
			@back="step = 1"
		/>

		<StepReviewStudents
			v-if="step === 3"
			:rows="csvDataRows"
			:datastudents="datastudents"
			:student-id-column-index="studentIdColumnIndex"
			:column-issues="columnIssues"
			:importing="importing"
			@select-student="selectStudent"
			@deselect-student="deselectStudent"
			@import="runSave"
			@back="step = 2"
		/>

		<StepResults
			v-if="step === 4"
			:import-results="importResults"
			@done="finish"
		/>
	</div>
</template>

<script>
import Papa from 'papaparse';
import StepIndicator from 'src/components/Util/StepIndicator.vue';
import StepPreview from 'src/components/Util/Upload/StepPreview.vue';
import StepMapColumns from 'src/components/Thirdparty/Upload/StepMapColumns.vue';
import StepReviewStudents from 'src/components/Thirdparty/Upload/StepReviewStudents.vue';
import StepResults from 'src/components/Thirdparty/Upload/StepResults.vue';

const STUDENT_ID_COLUMN_HINTS = ['studentid', 'student id', 'student_id', 'student number', 'username', 'login'];

export default {
	components: {
		StepIndicator,
		StepPreview,
		StepMapColumns,
		StepReviewStudents,
		StepResults,
	},
	data() {
		return {
			step: 1,
			stepLabels: [this.$t('preview'), this.$t('map-columns'), this.$t('review-students')],

			colAssign: {},
			datatype: {},
			loadedCSV: null,
			datastudents: [],
			existingCols: {},
			existingColsArr: [],
			csvCols: null,
			cellsData: {},
			createdCols: [],
			createdColumnNames: [],
			exclusionList: ['Student'],
			isDate: {},
			students: [],

			studentIdColumnIndex: null,

			preview: null,
			loadingPreview: false,

			importing: false,
			importResults: null,
		}
	},

	mounted() {
		this.$users.getStudents.call(this).then(students => {
			this.students = students;
		})
		this.getExisitingCols();
	},
	methods: {
		getExisitingCols() {
			//get existing columns from the API endpoint and saves them into an aray
			this.$cfetch(`${this.$settings.APIBaseURL}/thirdpartydata/getcols`)
				.then(res => {
					this.existingCols = res.result;
					this.existingColsArr = Object.keys(this.existingCols).map(key => res.result[key].caption);
				});
		},
		handleSelection(index) {
			//Save all the created columns
			if (this.colAssign[index] == 'create' && this.csvCols[index] !== null) {
				this.createdCols[index] = this.csvCols[index];
			}

			if (this.colAssign[index] == 'ignore') {
				this.$delete(this.datatype, index);
			} else if (this.colAssign[index] != '' && this.datatype[index] === undefined) {
				// Re-detect if it was previously cleared (e.g. column was ignored, then re-mapped)
				this.$set(this.datatype, index, this.detectColumnDatatype(Number(index)));
			}
		},
		onMapColumnsNext() {
			this.matchStudents();
			this.step = 3;
		},
		matchStudents() {
			if (this.studentIdColumnIndex === null) return;

			this.loadedCSV.slice(1).forEach((row, index) => {
				if (this.datastudents[index].manual) return;

				const rawValue = row[this.studentIdColumnIndex];
				if (rawValue === null || rawValue === undefined || rawValue === '') return;

				const normalized = String(rawValue).trim().toLowerCase();
				const match = this.students.find(s => s.studentid && String(s.studentid).trim().toLowerCase() === normalized)
					|| this.students.find(s => s.username && String(s.username).trim().toLowerCase() === normalized);

				this.datastudents[index].id = match ? match.id : null;
				this.datastudents[index].displayName = match ? match.displayName : null;
			});
		},
		async runSave() {
			this.importing = true;
			this.createdColumnNames = [];
			try {
				for (const [index, col] of this.createdCols.entries()) {
					if (Number(index) === this.studentIdColumnIndex) continue;
					if (this.colAssign[index] === 'create' && col !== '') {
						await this.addColumn(col, index);
						this.createdColumnNames.push(col);
					}
				}
				await this.mapTable();
			} finally {
				this.importing = false;
			}
		},
		mapTable() {
			this.cellsData = {};
			/* We map the CSV array into an object. Note that the data is being labeled
				with the student and datapoint ids, respectively */
			this.loadedCSV.slice(1).forEach((row, index) => {
				const student = this.datastudents[index];
				const studentId = student.id;
				if (studentId !== null) {
					if (!this.cellsData[studentId]) {
						this.cellsData[studentId] = {};
					}
				}

				Object.keys(row).forEach((columnIndex) => {
					const selectedOption = this.reactiveColAssign[columnIndex];
					// Exclude ignored columns and rows without students
					if(selectedOption && selectedOption.id && studentId !== null &&

						selectedOption !== 'ignore' && row[columnIndex] !== null) {
						const colId = selectedOption.id;

                        if(row[columnIndex] == null || row[columnIndex] == ''){
                            this.cellsData[studentId][colId] = {
                                value: null,
                                datetime: null
                            };
                        } else {
                            if(this.isValidDateTime(row[columnIndex])){
                                this.cellsData[studentId][colId] = {
                                    value: null,
                                    datetime: this.formatDatetimeToYmdHis(row[columnIndex])
                                }
                            } else {
                                this.cellsData[studentId][colId] = {
                                    value: row[columnIndex],
                                    datetime: null
                                }
                            }
                        }
					}
				});
			});


			// We save the object in the DB
			if (Object.keys(this.cellsData).length > 0) {
				return this.saveTransferData(this.cellsData);
			}
			return Promise.resolve();
		},
		onFileSelected(file) {
			this.loadingPreview = true;
			new Promise((resolve, reject) => {
				Papa.parse(file, {
					complete: res => {
						this.loadedCSV = res.data;
                        //Hydrate
						this.loadedCSV.slice(1).forEach((row, index) => {

							this.$set(this.datastudents, index, {
								id: null,
								displayName: null,
								manual: false,
							});
                        });
                        //Check if a column is a datetime entry
                        this.loadedCSV[1].forEach((item,index) => {

                            const nullItem =  item == null || item == '';

                            if(this.isValidDateTime(item)){
                                this.isDate[index]= true;
                            } else {
                                this.isDate[index]= false;
                            }

                            if(nullItem) this.isDate[index]= null;
                        });
						resolve(this.loadedCSV);
					},
					error: error => {
						console.error('Error parsing CSV file:', error);
						reject(error);
					}
				});
			})
				.then(parsedCSV => {
					// Save the columns from the CSV file
					this.csvCols = parsedCSV[0];
					this.createdCols = Array(this.csvCols.length).fill('');

                    //Hydrate datatype
                    this.hydrateDatatype();
					// Assign values to colAssign based on matching headers in existingCols,
					//if any of the elements is in the exclusionList, then its select is set to ignore
					this.csvCols.forEach((header, index) => {
						let found = false;
						for (const key in this.existingCols) {
							const existingCol = this.existingCols[key];
							if (existingCol.caption === header) {
								this.$set(this.colAssign, index, existingCol);
								found = true;
								break;
							}
						}
						if (!found) {
							if (this.exclusionList.includes(header)) {
								this.$set(this.colAssign, index, 'ignore');
							} else {
								this.$set(this.colAssign, index, '');
							}
						}
					});

					this.studentIdColumnIndex = this.detectStudentIdColumnIndex(this.csvCols);

					this.preview = {
						file_name: file.name,
						row_count: this.loadedCSV.length - 1,
						column_count: this.csvCols.length,
						headers: this.csvCols,
						rows: this.loadedCSV.slice(1, 6),
					};
				})
				.catch(error => {
					console.error('Error parsing CSV file:', error);
				})
				.finally(() => { this.loadingPreview = false; });
		},
		detectStudentIdColumnIndex(headers) {
			const lower = headers.map(h => (h || '').toLowerCase().trim());
			for (const hint of STUDENT_ID_COLUMN_HINTS) {
				const idx = lower.findIndex(h => h.includes(hint));
				if (idx !== -1) return idx;
			}
			return null;
		},
		selectStudent(index) {
			const self = this;
			this.$users.getStudents.call(this).then(students => {
				this.$store.commit("openModal", {
					type: "selector",
					title: this.$t("select-a-student"),
					selectCallback: function (selection) {
						self.datastudents[index].id = selection.id;
						self.datastudents[index].displayName = selection.displayName;
						self.datastudents[index].manual = true;
						self.$store.commit("closeModal");
					},
					selected: [
						{
							id: self.datastudents[index].id
						}
					],
					data: students,
					captionKey: "displayName",
                    subcaptionKey: "studentid",
					studentSearchFilters: true
				});
			});
		},
		deselectStudent(index) {
			this.datastudents[index].id = null;
			this.datastudents[index].displayName = null;
			this.datastudents[index].manual = true;
		},
		checkRepeatedColId(input) {
			for (let index in this.existingCols) {
				if (input == this.existingCols[index].name) {
					return true;
				}
			}
		},
		checkBlankColName: function (input) {
			if (input === '-' || input === '') {
				return true;
			}
			else {
				return false;
			}
		},
		slugifyColumnName(input) {
			return (input || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+/, '').replace(/_+$/, '');
		},
		addColumn(input, index) {
			return new Promise((resolve, reject) => {
                var name = this.slugifyColumnName(input);
				if (this.checkRepeatedColId(name)) {
					this.$store.commit("openDialog", {
						title: this.$t("oops"),
						content: this.$t("column-name-already-in-use")
					});
					reject();
				} else if (this.checkBlankColName(name)) {
					this.$store.commit("openDialog", {
						title: this.$t("oops"),
						content: this.$t("the-name-can-not-be-empty")
					});
					reject();
				} else {
					this.$cfetch(`${this.$settings.APIBaseURL}/thirdpartydata/save`, {
						data: {
							id: null,
							name: name,
							caption: input,
                            datatype: this.datatype[index]
						},
						method: 'post'
					}).then((res) => {
						if (res.status === "SUCCESS") {
							const colKey = index;
							this.$set(this.colAssign, colKey, res.result);
							resolve();
						} else {
							if (res.error_message) {
								this.error = res.error_message;
							} else {
								this.error = this.$t("failed-load-table");
							}
							reject();
						}
					}).catch((err) => {
                        console.error(err)
                        this.$log.error.call(this, err)
                    });
				}
			})
		},
		saveTransferData(input) {
            // Attach the column ids to the datatype
            for(let index in this.datatype){
                let type = this.datatype[index];
                let colId = this.colAssign[index].id;

                this.datatype[colId] = type;

                //if(Number(index) !== Number(colId)) delete this.datatype[index];

            }

			const savedRows = this.datastudents.filter(s => s.displayName).length;
			const skippedRows = this.datastudents.filter(s => !s.displayName).length;

			return this.$cfetch(`${this.$settings.APIBaseURL}/thirdpartydata/savevalues`, {
				method: 'post',
				data: {
					values: input,
                    datatype: this.datatype
				}
			}).then((res) => {
				if (res.status === "SUCCESS") {
					this.importResults = {
						saved_rows: savedRows,
						skipped_rows: skippedRows,
						created_columns: this.createdColumnNames,
					};
					this.step = 4;
				} else {
					if (res.error_message) {
						this.error = res.error_message;
					} else {
						this.error = this.$t("failed-to-save-values");
					}
					this.$store.commit("openDialog", {
						title: this.$t("oops"),
						content: this.error
					});
				}
			});
		},
        isValidDateTime(datetimeString) {
            // Regular expression for Y-m-d H:i:s  format
            const datetimeRegex = /^\d{4}[-\/]\d{2}[-\/]\d{2} \d{2}:\d{2}:\d{2}$/; //eslint-disable-line
            const dateRegex = /^\d{4}[-\/]\d{2}[-\/]\d{2}$/; //eslint-disable-line
            const excelRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/; //eslint-disable-line

            return datetimeRegex.test(datetimeString) || excelRegex.test(datetimeString)
                || dateRegex.test(datetimeString) ;
        },
        formatDatetimeToYmdHis(inputDate) {
            // Ensure that the inputDate is a Date object
            if (!(inputDate instanceof Date)) {
                inputDate = new Date(inputDate);
            }
            // Get individual components
            const year = inputDate.getFullYear();
            const month = String(inputDate.getMonth() + 1).padStart(2, '0'); // Because months are zero-based
            const day = String(inputDate.getDate()).padStart(2, '0');
            const hours = String(inputDate.getHours()).padStart(2, '0');
            const minutes = String(inputDate.getMinutes()).padStart(2, '0');
            const seconds = String(inputDate.getSeconds()).padStart(2, '0');

            // Format the date string
            const formattedDate = hours !== '00' || minutes !== '00' || seconds !== '00'
                ? `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
                : `${year}-${month}-${day} 00:00:01`;

            return formattedDate;
        },
        hydrateDatatype(){
            for (let index in this.csvCols){
                this.$set(this.datatype, index, this.detectColumnDatatype(Number(index)))
            }
        },
        detectColumnDatatype(columnIndex) {
            const values = this.loadedCSV.slice(1)
                .map(row => row[columnIndex])
                .filter(v => v !== null && v !== undefined && v !== '');

            if (!values.length) return '';
            if (values.every(v => this.isValidDateTime(v))) return '1';
            if (values.every(v => this.isNumeric(v))) return '0';
            return '2';
        },
        isNumeric(value) {
            return typeof value === 'string' && value.trim() !== '' && !isNaN(Number(value));
        },
		finish() {
			this.$router.replace("/thirdparty/edit");
		},
	},
	computed: {
		csvDataRows() {
			return this.loadedCSV ? this.loadedCSV.slice(1) : [];
		},
		mappingValid() {
			const isSelectAssigned = Object.keys(this.colAssign)
				.filter(i => Number(i) !== this.studentIdColumnIndex)
				.every(i => this.colAssign[i] !== '');
            const isDatatypeAssigned = Object.keys(this.datatype)
				.filter(i => Number(i) !== this.studentIdColumnIndex)
				.every(i => this.datatype[i] !== '');

			return isSelectAssigned && isDatatypeAssigned;
		},
		columnIssues() {
			const issues = [];
			const seenNames = new Set();
			this.createdCols.forEach((rawName, index) => {
				if (Number(index) === this.studentIdColumnIndex) return;
				if (this.colAssign[index] !== 'create') return;

				const name = this.slugifyColumnName(rawName);
				if (this.checkBlankColName(name)) {
					issues.push(`${this.$t('the-name-can-not-be-empty')} (${this.$t('col')} ${index})`);
				} else if (this.checkRepeatedColId(name) || seenNames.has(name)) {
					issues.push(`${this.$t('column-name-already-in-use')} (${this.$t('col')} ${index})`);
				}
				seenNames.add(name);
			});
			return issues;
		},
		reactiveColAssign() {
			return this.colAssign;
		}
	}
};
</script>
